import classNames from 'classnames';
import { Menu, Transition } from '@headlessui/react';
import React, { forwardRef, Fragment, Ref } from 'react';
import { BiChevronDown } from '@react-icons/all-files/bi/BiChevronDown';
import { CgSpinner } from '@react-icons/all-files/cg/CgSpinner';
import { trackEvent } from '../analytics';

interface DropdownMenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  segmentName?: string;
}

export function DropdownMenuButton(props: DropdownMenuButtonProps) {
  return (
    <button
      {...props}
      type="button"
      className={classNames(
        'w-full flex items-center space-x-2 py-2.5 px-4',
        props.className,
      )}
      onClick={(e) => {
        trackEvent(props.segmentName);
        props.onClick?.(e);
      }}
    />
  );
}

export const dropdownMenuHeaderSizes = {
  normal: 'py-3 pr-2 pl-4',
  small: 'py-0.5 pr-1 pl-2 text-xs max-h-full self-center',
};

export interface DropdownMenuProps {
  children?: React.ReactNode;
  heading: React.ReactNode;
  className?: string;
  buttonContainerClassname?: string;
  truncatedHeader?: boolean;
  size?: keyof typeof dropdownMenuHeaderSizes;
  alignDropdownRight?: boolean;
  isLoading?: boolean;
  verticalButtonPadding?: boolean;
  headerClassnames?: string;
  disabled?: boolean;
  openUpwards?: boolean;
  onHeaderClick?: (args: any) => any;
  hideIcon?: boolean;
  headerAnalyticsProperties?: {
    name: string;
    properties?: Record<string, any>;
  };
  error?: any;
}

export const DropdownMenu = forwardRef(
  (
    {
      size = 'normal',
      alignDropdownRight = true,
      verticalButtonPadding = true,
      ...props
    }: DropdownMenuProps,
    ref: Ref<HTMLDivElement>,
  ) => (
    <Menu
      ref={ref}
      as="div"
      className={classNames(
        'relative block text-left bg-white rounded-md z-00',
        props.className,
        props.error && 'border border-red-300',
      )}
    >
      {({ open }) => (
        <>
          <Menu.Button
            className={classNames(
              'font-medium inline-flex items-center justify-between w-full rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-dark whitespace-nowrap transition-all',
              dropdownMenuHeaderSizes[size],
              !props.isLoading && props.headerClassnames,
              props.disabled && 'pointer-events-none bg-gray-100',
              props.hideIcon && '!px-5',
            )}
          >
            <div
              className={classNames(
                'flex items-center text-left',
                props.hideIcon ? 'w-full' : 'w-5/6',
              )}
              onClick={() => {
                trackEvent(
                  props.headerAnalyticsProperties?.name,
                  props.headerAnalyticsProperties?.properties,
                );
              }}
            >
              <span
                className={classNames(
                  props.truncatedHeader ? 'truncate' : 'contents',
                )}
              >
                {props.isLoading ? 'Updating' : props.heading}
              </span>
            </div>

            {props.isLoading ? (
              <CgSpinner className="animate-spin my-auto ml-2" size={20} />
            ) : (
              !props.hideIcon && <BiChevronDown size={20} />
            )}
          </Menu.Button>
          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-100 translate-x-50"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              static
              className={classNames(
                'origin-top-right absolute w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none max-h-60 overflow-y-scroll z-10',
                props.buttonContainerClassname,
                alignDropdownRight ? 'right-0' : 'left-0',
                props.openUpwards && 'bottom-full mb-0.5',
              )}
            >
              {React.Children.map(props.children, (child, i) =>
                child ? (
                  <Menu.Item key={i}>
                    {({ active }) => (
                      <div
                        className={classNames(
                          'w-full text-left',
                          active
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700',
                          'block text-sm',
                        )}
                      >
                        {child}
                      </div>
                    )}
                  </Menu.Item>
                ) : null,
              )}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  ),
);
